import { computed } from 'vue';
import { NetworkStatus } from '@apollo/client/core';
import { useQuery } from '@vue/apollo-composable';
import { FETCH_FAVORITE_SITTERS } from '@/graphql/account/queries';
import type { Ref } from 'vue';
import type { FavoriteSittersQuery, FavoriteSittersVariables } from './types';

export function useFavoritesQuery(variables: Readonly<Ref<FavoriteSittersVariables>>) {
  const { result, loading, error, networkStatus, refetch, stop, start, onResult } = useQuery<
    FavoriteSittersQuery,
    FavoriteSittersVariables
  >(FETCH_FAVORITE_SITTERS, variables, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    // A post-removal refetch must not reuse a request started before the mutation.
    context: { queryDeduplication: false },
  });
  const connection = computed(() => result.value?.viewer?.favoriteSitters ?? null);
  const isLoading = computed(
    () =>
      loading.value &&
      (networkStatus.value === undefined ||
        networkStatus.value === NetworkStatus.loading ||
        networkStatus.value === NetworkStatus.setVariables)
  );
  const hasError = computed(() => !!error.value && !connection.value);
  const refreshError = computed(() => !!error.value && !!connection.value);

  function onLoaded(callback: () => void) {
    return onResult(({ networkStatus: status }) => {
      if (status === NetworkStatus.ready) callback();
    });
  }

  let pendingRefresh = Promise.resolve();

  function refreshFavorites(): Promise<void> {
    pendingRefresh = pendingRefresh.then(refetchAfterRemoval);
    return pendingRefresh;
  }

  async function refetchAfterRemoval(): Promise<void> {
    // Apollo 3 refetches can still receive an older in-flight query's result.
    if (isLoading.value) {
      stop();
      start();
      return;
    }
    try {
      await refetch();
    } catch {
      // useQuery exposes the error; a failed refresh does not undo a successful removal.
    }
  }

  return { connection, isLoading, hasError, refreshError, refreshFavorites, onLoaded };
}
